import { Fragment } from "react";

import { cn } from "../lib/util.js";

/* 릴과 토스트는 지도 아래 같은 자리를 쓴다. 둘이 겹치지 않게 App 이 하나씩만 띄운다.
   화면(viewport)이 아니라 지도를 기준으로 가운데를 잡는다 — 랜덤이 지도 위 버튼이 된
   뒤로는 결과도 그 지도 안에서 나와야 한다. 화면 기준으로 두면 넓은 화면에서 왼쪽
   목록 폭의 절반만큼 왼쪽으로 밀려서, 지도만 보고 있으면 삐뚤어져 보인다.
   그래서 MapFrame 안에 산다(fixed → absolute). 세로 자리는 그대로다 — 지도는 아래쪽
   끝이 화면 끝이라, 지도 바닥에서 띄우나 화면 바닥에서 띄우나 같은 높이다. */
const PILL = "absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full px-[18px] py-[9px] shadow-card";

/* 지도 위에 떠 있는 버튼들의 공통 껍데기 (UserButton, RecenterButton).
   바탕이 불투명해야 한다 — 이 앱의 보조 버튼(quiet)은 bg-transparent 라서
   지도 타일 위에서는 내용이 안 읽힌다. 한 곳에 둬야 둘이 따로 놀지 않는다. */
export const FLOAT_BTN = "border border-border bg-background shadow-card hover:bg-muted";

/* 켜진 상태의 떠 있는 토글 (지도 고정, 내 위치). 파랗게 차는 것으로만 말한다 —
   상태마다 다른 아이콘을 쓰면 같은 버튼이 두 개처럼 보여서 무엇이 켜진 모양인지
   흐려진다. FLOAT_BTN 과 마찬가지로 한 곳에 둬야 둘이 따로 놀지 않는다. */
export const FLOAT_ON =
  "data-[state=on]:border-primary data-[state=on]:bg-primary " +
  "data-[state=on]:text-primary-foreground data-[state=on]:hover:bg-primary";

/* 지도 위 오른쪽 위에 세로로 쌓이는 버튼 열 (계정, 회사로 되돌리기, 랜덤 한 쌍).
   자리를 여기서 한 번만 정하는 이유: 각 버튼이 제 좌표를 들고 있으면 간격이
   상수로 박히는데, 로그인 버튼은 알약↔원형으로 폭이 바뀌어서 그 상수가 틀어진다.
   items-end 라 폭이 달라도 오른쪽 끝은 항상 맞는다.

   **높이가 이 열의 제약이다.** 지도틀에는 overflow 가 없어서, 열이 지도보다 길면 버튼이
   그대로 아래로 삐져나온다 — 한 번은 문서가 화면보다 111px 길어져서 페이지 전체가 세로로
   흔들렸다. 그래서 무엇을 넣을지 정할 때마다 이 숫자를 다시 센다.
   지금은 넓은 화면 180px, 좁은 화면 132px 이다(회사로 되돌리기가 빠진다). 폰의 지도
   46dvh 안에 든다 — '내 위치'를 아래 모서리로 내보내고 나서 랜덤 한 쌍이 여기 돌아올 수
   있었던 것도 그 덕이다(MapCorner).

   목록을 펴는 동안에는 열을 통째로 접는다. 그때 지도는 19dvh 라, 작은 폰에서는 줄인
   132px 마저 안 들어간다. 지도를 다루는 손잡이들이라, 목록을 보고 있는 동안 못 눌러도
   아쉬울 게 없다. */
export function MapChrome({ className, children }) {
  return (
    <div className={cn("absolute right-3 top-3 z-20 flex flex-col items-end gap-2", className)}>
      {children}
    </div>
  );
}

/* 지도 위 오른쪽 **아래** 모서리 ('내 위치').
 *
 * 위 열과 갈라 놓는 이유는 자리가 곧 뜻이라서다. 위쪽 열은 지도에 무엇을 할지 고르는
 * 손잡이들(계정·회사로·랜덤)이고, '내 위치'는 지도를 보는 내내 엄지로 눌러야 하는
 * 버튼이다. 폰을 한 손으로 쥐면 위쪽 모서리는 엄지가 안 닿는다 — 지도 앱들이 하나같이
 * 이 버튼만 아래에 두는 이유고, 이 앱도 그 자리를 따른다.
 *
 * 위 열과 달리 접히는 걸 걱정할 필요가 없다. 저쪽은 아래로 자라서 지도 밖으로 삐져나갔지만
 * (MapChrome 주석) 이쪽은 바닥에 붙어 있어서 지도가 아무리 얇아져도 넘칠 데가 없다.
 *
 * 릴·토스트(PILL)가 이 버튼 위를 지나간다. 둘 다 가로 가운데라 폭이 넓으면 오른쪽 끝이
 * 여기까지 오는데, z-50/60 이라 버튼을 덮는다. 그대로 뒀다 — 잠깐 떴다 사라지는 것들이고,
 * 릴이 도는 2초 동안 내 위치를 누를 일은 없다. 피하자고 버튼을 위로 올리면 남는 22시간
 * 59분 동안 엄지에서 멀어진다. */
export function MapCorner({ className, children }) {
  return (
    <div className={cn("absolute right-3 bottom-3 z-20 flex flex-col items-end gap-2", className)}>
      {children}
    </div>
  );
}

/** 지도 아래 릴. 이름이 넘어갈 때마다 노드를 갈아끼워야 슬라이드-인이 다시 돈다 → key={n} */
export function Reel({ reel }) {
  if (!reel) return null;
  return (
    <div
      className={cn(
        PILL,
        "reel z-[60] flex max-w-[calc(100%-32px)] items-baseline gap-[9px] whitespace-nowrap px-5",
        "bg-foreground text-ground",
        /* 알약이 곧 릴 창이다. 여기서 잘라야 이름이 아래에서 올라오는 것처럼 보인다 —
           이름 쪽에 걸면 자기 자신과 함께 움직여서 아무것도 안 잘린다. */
        "overflow-hidden",
        reel.win && "win animate-reel-win bg-primary",
      )}
    >
      <span className={cn("rl-tag flex-none text-[11px]", reel.win ? "opacity-100" : "opacity-65")}>
        {reel.tag}
      </span>
      <span
        key={reel.n}
        className="rl-name min-w-0 animate-reel-in overflow-hidden text-ellipsis text-[15px] font-bold tracking-[-.01em]"
      >
        {reel.name}
      </span>
    </div>
  );
}

export function Toast({ toast }) {
  if (!toast) return null;
  return (
    <div className={cn(PILL, "toast z-50 bg-foreground text-[13.5px] font-semibold text-ground")}>
      {toast.msg}
    </div>
  );
}

/* 지도 컨테이너와 안내문을 형제로 둔다. 카카오 SDK가 컨테이너 내용을 갈아엎기 때문.
 *
 * compact = 좁은 화면에서 목록을 펴놨다. 그때는 지도가 자리를 내준다 — 안 내주면
 * 46dvh 를 붙들고 있느라 정작 펴놓은 목록에 카드 한 장 높이밖에 안 남는다(실측 87px).
 * 지도를 아예 감추지는 않는다. 접는 손잡이 바로 아래에 지도가 조금이라도 보여야
 * '목록이 지도를 덮고 있다'가 읽히고, display:none 으로 껐다 켜면 카카오가 컨테이너
 * 크기를 0 으로 기억해서 다시 폈을 때 relayout() 을 불러줘야 한다.
 */
export function MapFrame({ containerRef, notice, compact, children }) {
  return (
    <div className={cn("mapwrap relative min-w-0 bg-muted wide:min-h-60",
                       compact ? "min-h-[19dvh]" : "min-h-[46dvh]")}>
      <div className="absolute inset-0" id="map" ref={containerRef} />
      <div
        className="mapmsg absolute inset-0 grid place-items-center bg-muted p-[30px] text-center"
        id="mapmsg"
        hidden={!notice}
      >
        {notice && (
          <div className="max-w-[34ch] text-[13.5px] leading-[1.9] text-muted-foreground">
            {notice.map((line, i) => (
              <Fragment key={i}>
                {i > 0 && <br />}
                {/* 여기만 mono 를 남긴다 — 환경변수 이름을 그대로 보여주는 진짜 코드고,
                    ASCII 뿐이라 한글 글리프가 없어 폰트가 갈라지는 문제가 애초에 없다. */}
                {line.startsWith("`")
                  ? <code className="rounded bg-background px-1.5 py-0.5 font-mono text-xs">
                      {line.replace(/`/g, "")}
                    </code>
                  : line}
              </Fragment>
            ))}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}
